import { useQuery } from 'convex/react'
import { useEffect, useMemo, useState } from 'react'
import { api } from '@ppwr/convex/_generated/api'
import type { Id } from '@ppwr/convex/_generated/dataModel'
import type { PublicCommunityOccurrenceRow } from '@ppwr/convex/gameSchedules/publicCommunityTypes'
import { CARD_VISIBILITY_WINDOW_MS, LIVE_DATA_HORIZON_MS } from './gameStatus'

/**
 * Loads public community schedule occurrences with two scoped Convex subscriptions:
 * - `getPublicScheduleDefinitions` reads only `gameSchedules` so it is NOT invalidated
 *   by player joins/leaves; it changes only when an admin edits a schedule.
 * - `getPublicGameLiveData` reads `games` + `players` only for schedules with a
 *   near-term occurrence, so its invalidation fan-out is bounded.
 */
export function usePublicSchedules(): {
  upcomingGames: PublicCommunityOccurrenceRow[] | undefined
} {
  const [hotScheduleIds, setHotScheduleIds] = useState<Id<'gameSchedules'>[]>([])

  const definitions = useQuery(api.gameSchedules.queries.getPublicScheduleDefinitions, {})

  useEffect(() => {
    const recomputeHotIds = () => {
      if (!definitions?.length) {
        setHotScheduleIds([])
        return
      }
      const now = Date.now()
      const ids = new Set<string>()
      for (const row of definitions) {
        const t = row.nextOccurrence
        if (t >= now - CARD_VISIBILITY_WINDOW_MS && t <= now + LIVE_DATA_HORIZON_MS) {
          ids.add(row.schedule._id)
        }
      }
      setHotScheduleIds([...ids] as Id<'gameSchedules'>[])
    }

    recomputeHotIds()
    const intervalId = setInterval(recomputeHotIds, 60_000)
    return () => clearInterval(intervalId)
  }, [definitions])

  const liveData = useQuery(
    api.gameSchedules.queries.getPublicGameLiveData,
    hotScheduleIds.length > 0 ? { scheduleIds: hotScheduleIds } : 'skip'
  )

  const upcomingGames = useMemo((): PublicCommunityOccurrenceRow[] | undefined => {
    if (definitions === undefined) return undefined
    const liveList = liveData ?? []
    type LiveRow = (typeof liveList)[number]
    const liveByKey = new Map<string, LiveRow>()
    for (const r of liveList) {
      liveByKey.set(`${r.scheduleId}_${r.nextOccurrence}`, r)
    }
    return definitions.map((def) => {
      const key = `${def.schedule._id}_${def.nextOccurrence}`
      const live = liveByKey.get(key)
      return {
        schedule: def.schedule,
        nextOccurrence: def.nextOccurrence,
        createdGames: live?.createdGames ?? def.createdGames,
        ...(live?.winnerNames?.length ? { winnerNames: live.winnerNames } : {})
      }
    })
  }, [definitions, liveData])

  return { upcomingGames }
}
